// src/components/ui/Layout/Stack.tsx
// Micro layout: Vertical spacing primitive following IonicLayout.md

import React, { forwardRef } from 'react';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export type SpacingToken = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type StackDirection = 'vertical' | 'horizontal';
export type StackAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';
export type StackJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Stack direction (default: 'vertical') */
  direction?: StackDirection;
  /** Gap between children */
  gap?: SpacingToken;
  /** Cross-axis alignment */
  align?: StackAlign;
  /** Main-axis justification */
  justify?: StackJustify;
  /** Allow wrapping */
  wrap?: boolean;
  /** Full width (applies to vertical stacks) */
  fullWidth?: boolean;
  children: React.ReactNode;
}

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const gapMap: Record<SpacingToken, string> = {
  none: '0',
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
};

const alignMap: Record<StackAlign, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
  baseline: 'baseline',
};

const justifyMap: Record<StackJustify, string> = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  between: 'space-between',
  around: 'space-around',
  evenly: 'space-evenly',
};

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Stack = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = 'vertical',
      gap = 'none',
      align = 'stretch',
      justify = 'start',
      wrap = false,
      fullWidth = true,
      children,
      className = '',
      style,
      ...rest
    },
    ref
  ) => {
    const isVertical = direction === 'vertical';

    const computedStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: isVertical ? 'column' : 'row',
      alignItems: alignMap[align],
      justifyContent: justifyMap[justify],
      gap: gapMap[gap],
      flexWrap: wrap ? 'wrap' : 'nowrap',
      width: isVertical && fullWidth ? '100%' : undefined,
      ...style,
    };

    return (
      <div
        ref={ref}
        style={computedStyle}
        className={className}
        data-layout="Stack"
        data-direction={direction}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

Stack.displayName = 'Stack';

export default Stack;
